import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { BlogCard } from '@chicare/shared';

import { api } from '@/lib/api';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Icon } from '@/components/icons/Icon';
import { SearchField } from '@/components/ui/SearchField';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/prose';
import { sectorParam, sectorPath, useSector } from '@/lib/sector';

/**
 * The blog index.
 *
 * ── IT IS SECTOR-AWARE, AND THAT IS NOT COSMETIC ────────────────────────────
 *
 * `/blog` is the human shelf and `/vet/blog` is the animal-health one. An
 * article about poultry withdrawal periods must not appear among articles about
 * children's fever, for the same reason cattle ivermectin must not appear in a
 * search for "dewormer": the difference is measured in hospital admissions.
 *
 * The server does the filtering. This screen only has to pass the sector it is
 * standing in — and the one bug worth guarding against is forgetting to, which
 * would quietly show the human shelf on the veterinary side.
 *
 * ── THE FIRST ARTICLE IS BIGGER THAN THE REST ───────────────────────────────
 *
 * Only on the first page, and only when nothing is being filtered. A "featured"
 * card that is really just "whatever is newest" is honest when the list is in
 * date order and a lie once somebody has searched, so it stands down.
 */
export function BlogScreen() {
  const sector = useSector();
  const path = (to: string) => sectorPath(sector, to);

  const [params, setParams] = useSearchParams();
  const category = params.get('category');
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);

  // The box is local and the URL is the source of truth, so a typed letter does
  // not push a history entry per keystroke. Enter, or the button, commits.
  const [term, setTerm] = useState(params.get('q') ?? '');
  const query = params.get('q');

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['blog', sector, category, query, page],
    queryFn: ({ signal }) =>
      api.blog({ sector: sectorParam(sector), category, q: query, page }, signal),
  });

  const commit = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params);

    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }

    // Any change of filter starts at the beginning. Page 3 of a search nobody
    // has run yet is an empty screen that looks broken.
    if (!('page' in next)) merged.delete('page');

    setParams(merged, { replace: true });
  };

  const featured = !category && !query && page === 1 ? data?.posts[0] : undefined;
  const rest = featured ? (data?.posts ?? []).slice(1) : (data?.posts ?? []);

  return (
    <>
      {/* `back`, because somebody arrives here from the home screen or the
          footer mid-errand, reads one piece, and wants to carry on shopping.
          Without it the only way out on a phone is the browser chrome, and in
          a PWA or a webview there is no browser chrome. */}
      <AppBar back title="Blog" />

      <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-6">
        <header className="max-w-[58ch]">
          {/* The brand, on the heading.
              The page itself is fine — search, filters and cards all do their
              job — but the title sat as plain black text with nothing about it
              that said ChiCare. A rule in the brand gradient gives it weight
              without turning a reading page into a panel: the same two blues
              as the trust banner, /sell and the prescription screen, so this
              reads as one more room in the same building. */}
          <span
            aria-hidden
            data-testid="blog-accent"
            className="mb-3 block h-1 w-12 rounded-full bg-[linear-gradient(90deg,var(--color-blue-deep),var(--color-blue))]"
          />
          <h1 className="m-0 font-display text-[27px] font-bold leading-tight tracking-[-0.025em] text-ink sm:text-[34px]">
            Blog
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-muted sm:text-[15px]">
            {sector === 'veterinary'
              ? 'Dosing, withdrawal periods and herd health, written for the people who keep animals.'
              : 'Written by pharmacists and the ChiCare team. Medicines, safety, and how to use this platform.'}
          </p>
        </header>

        {/* ── Search and categories ───────────────────────────────────────
            One row on desktop, stacked on a phone. The search box is first in
            the DOM either way, because it is what somebody arriving from a
            Google result for a drug name is looking for. */}
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            commit({ q: term.trim() || null });
          }}
          className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center"
        >
          <SearchField
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onClear={() => {
              setTerm('');
              commit({ q: null });
            }}
            placeholder="Search the articles"
            aria-label="Search the articles"
            data-testid="blog-search"
            containerClassName="sm:max-w-[380px] flex-1"
          />
          <Button type="submit" variant="grey" icon="search" className="sm:w-auto">
            Search
          </Button>
        </form>

        {(data?.categories.length ?? 0) > 0 ? (
          <nav
            aria-label="Article categories"
            className="mt-3 flex flex-wrap gap-1.5"
            data-testid="blog-categories"
          >
            <CategoryChip active={!category} onClick={() => commit({ category: null })}>
              Everything
            </CategoryChip>
            {data?.categories.map((one) => (
              <CategoryChip
                key={one}
                active={category === one}
                onClick={() => commit({ category: category === one ? null : one })}
              >
                {one}
              </CategoryChip>
            ))}
          </nav>
        ) : null}

        {isError ? (
          <ErrorState
            title="We couldn't load the articles"
            body="Check your connection and try again."
            action={
              <Button icon="refresh" onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : isLoading ? (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((one) => (
              <div key={one}>
                <Skeleton className="aspect-[16/10] w-full rounded-card" />
                <Skeleton className="mt-3 h-4 w-3/4" />
                <div className="mt-2">
                  <SkeletonText lines={2} />
                </div>
              </div>
            ))}
          </div>
        ) : (data?.posts.length ?? 0) === 0 ? (
          <EmptyState
            title={
              query
                ? `Nothing matched “${query}”`
                : category
                  ? `Nothing in “${category}” yet`
                  : 'No articles yet'
            }
            body={
              query || category
                ? 'Try a shorter phrase, or clear the filters.'
                : 'The first ones are being written. Check back soon.'
            }
            // Never undefined. `EmptyState` asks for an action because an
            // empty state without a way out is a dead end, and "no articles
            // yet" on a public page is the most likely one to be seen by
            // somebody who arrived from a search engine.
            action={
              query || category ? (
                <Button
                  variant="grey"
                  onClick={() => {
                    setTerm('');
                    commit({ q: null, category: null });
                  }}
                >
                  Clear the search
                </Button>
              ) : (
                <Link
                  to={path('/search')}
                  className="inline-flex min-h-11 items-center rounded-btn bg-blue px-4 text-[13.5px] font-semibold text-white no-underline"
                >
                  Browse medicines
                </Link>
              )
            }
          />
        ) : (
          <>
            {featured ? <FeaturedArticle post={featured} to={path(`/blog/${featured.slug}`)} /> : null}

            <div
              className={cn(
                'grid gap-x-5 gap-y-7 sm:grid-cols-2 lg:grid-cols-3',
                featured ? 'mt-7' : 'mt-6',
                isFetching && 'opacity-60 transition-opacity',
              )}
              data-testid="blog-list"
            >
              {rest.map((post) => (
                <ArticleCard key={post.slug} post={post} to={path(`/blog/${post.slug}`)} />
              ))}
            </div>

            {(data?.pages ?? 1) > 1 ? (
              <nav
                aria-label="More articles"
                className="mt-9 flex items-center justify-between gap-3 border-t border-line pt-5"
              >
                <Button
                  variant="grey"
                  icon="back"
                  disabled={page <= 1}
                  onClick={() => commit({ page: page > 2 ? String(page - 1) : null })}
                  className="w-auto"
                >
                  Newer
                </Button>

                <span className="text-[12px] text-faint">
                  Page {data?.page} of {data?.pages}
                </span>

                <Button
                  variant="grey"
                  disabled={page >= (data?.pages ?? 1)}
                  onClick={() => commit({ page: String(page + 1) })}
                  className="w-auto"
                >
                  Older
                </Button>
              </nav>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'min-h-9 rounded-full border px-3 text-[12.5px] font-medium',
        active
          ? 'border-blue bg-blue text-white'
          : 'border-line bg-white text-muted hover:border-blue hover:text-blue',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The newest article, given the room it is worth.
 *
 * Two columns from 768px up and one below, where a half-width picture and a
 * half-width headline would both be too small to be either.
 */
function FeaturedArticle({ post, to }: { post: BlogCard; to: string }) {
  return (
    <Link
      to={to}
      data-testid="blog-featured"
      className="group mt-6 grid gap-4 rounded-card border border-line bg-white p-4 no-underline md:grid-cols-[1.25fr_1fr] md:items-center md:gap-7 md:p-6"
    >
      <Cover post={post} className="aspect-[16/9] md:aspect-[16/10]" />

      <div className="min-w-0">
        <Meta post={post} />
        <h2 className="m-0 mt-2 font-display text-[20px] font-bold leading-snug tracking-[-0.02em] text-ink group-hover:text-blue sm:text-[25px]">
          {post.title}
        </h2>
        {post.excerpt ? (
          <p className="mt-2 text-[14px] leading-relaxed text-muted sm:text-[15px]">
            {post.excerpt}
          </p>
        ) : null}
        <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue">
          Read it
          <Icon name="chevron" size={14} />
        </span>
      </div>
    </Link>
  );
}

function ArticleCard({ post, to }: { post: BlogCard; to: string }) {
  return (
    <Link to={to} className="group block no-underline">
      <Cover post={post} className="aspect-[16/10]" />

      <div className="mt-3">
        <Meta post={post} />
        <h2 className="m-0 mt-1.5 font-display text-[16px] font-bold leading-snug tracking-[-0.015em] text-ink group-hover:text-blue">
          {post.title}
        </h2>
        {post.excerpt ? (
          <p className="mt-1.5 line-clamp-3 text-[13px] leading-relaxed text-muted">
            {post.excerpt}
          </p>
        ) : null}
      </div>
    </Link>
  );
}

/**
 * The picture, or a lettered placeholder.
 *
 * Not a grey box: a card with an empty rectangle above it reads as a failed
 * image load, and a writer who has not chosen a picture yet has not made a
 * mistake. The initial is drawn from the title, so two articles side by side
 * still look like two different articles.
 */
function Cover({ post, className }: { post: BlogCard; className?: string }) {
  if (post.cover?.url) {
    return (
      <img
        src={post.cover.url}
        alt={post.cover.alt ?? ''}
        loading="lazy"
        className={cn('w-full rounded-card border border-line object-cover', className)}
      />
    );
  }

  return (
    <div
      aria-hidden
      className={cn(
        'grid w-full place-items-center rounded-card border border-line bg-blue-tint',
        className,
      )}
    >
      <span className="font-display text-[34px] font-extrabold tracking-[-0.03em] text-blue/35">
        {post.title.trim().charAt(0).toUpperCase() || 'C'}
      </span>
    </div>
  );
}

/** Category, date and reading time, in that order of usefulness. */
function Meta({ post }: { post: BlogCard }) {
  return (
    <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-faint">
      {post.category ? (
        <span className="font-bold uppercase tracking-[0.06em] text-blue">{post.category}</span>
      ) : null}
      {post.published_at ? <span>{formatDate(post.published_at)}</span> : null}
      {post.reading_minutes ? <span>{post.reading_minutes} min read</span> : null}
    </p>
  );
}
